import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { me as fetchMe } from "../api/authService";
import { useRefresh } from "./RefreshContext";

export const AuthContext = createContext();

// `geofenced`, `addons` and `permissions` are all server-side facts an admin can
// flip mid-session; fetching once on mount froze them for the whole SPA session
// — that is the "geofence only locks after a refresh" bug. 60s matches the
// geofence location poll this has to start, so a fence switched on mid-session
// engages within one cycle instead of never.
const ME_REFRESH_MS = 60_000;
// Coalesce overlapping triggers — an interval tick, a tab regaining focus and an
// FCM push can all land together, and StrictMode double-mounts in dev.
const ME_MIN_GAP_MS = 15_000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // RefreshProvider wraps AuthProvider (App.js), so the FCM bus is available.
  const { refreshKey } = useRefresh();
  const lastMeAt = useRef(0);
  const meInFlight = useRef(false);

  // Re-pull /me so a mid-session change (fence assigned, addon toggled, perms
  // edited) reaches this tab without a reload. Throttled and in-flight guarded
  // because several triggers can fire at once. `force` skips only the throttle.
  const refreshMe = useCallback(async ({ force = false } = {}) => {
    if (!localStorage.getItem("access_token")) return null;
    if (meInFlight.current) return null;
    if (!force && Date.now() - lastMeAt.current < ME_MIN_GAP_MS) return null;
    meInFlight.current = true;
    try {
      // The axios interceptor unwraps `{ status, data }` for us, so `res` is
      // already that body — user sits at `res.data.user`, not
      // `res.data.data.user`. Same gotcha as the permissions catalog.
      const res = await fetchMe();
      const fresh = res?.data?.user;
      lastMeAt.current = Date.now();
      if (!fresh) return null;
      // Only touch state when something actually changed, so the steady-state
      // poll doesn't re-render every consumer once a minute for nothing.
      const nextJson = JSON.stringify(fresh);
      if (nextJson !== localStorage.getItem("user")) {
        setUser(fresh);
        localStorage.setItem("user", nextJson);
      }
      return fresh;
    } catch {
      // Silent — the axios interceptor already signs the user out on 401, and
      // anything else just leaves the cached payload until the next tick.
      return null;
    } finally {
      meInFlight.current = false;
    }
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem("access_token");
    let storedUser = null;
    try {
      const raw = localStorage.getItem("user");
      storedUser = raw ? JSON.parse(raw) : null;
    } catch {
      localStorage.removeItem("user");
    }
    setUser(storedUser);
    setAccessToken(storedToken || null);
    setIsLoading(false);

    // Background refresh of /me so the cached user (potentially saved before
    // permissions existed) picks up the latest perm set without forcing a
    // re-login. The throttle + in-flight guard inside refreshMe absorb
    // StrictMode's double invoke, so this stays a single request.
    if (storedToken) refreshMe();
  }, [refreshMe]);

  // Periodic + tab-focus refresh. This is what makes a fence assigned to an
  // already-signed-in employee take effect: it flips `geofenced`, which starts
  // the location poll in AppLayout.
  useEffect(() => {
    if (!accessToken) return;
    const id = setInterval(() => refreshMe(), ME_REFRESH_MS);
    // A backgrounded mobile PWA has its timers frozen outright, so the interval
    // alone can be arbitrarily late. Refresh the moment the tab comes back.
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshMe();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [accessToken, refreshMe]);

  // An FCM push means something changed server-side — free acceleration on top
  // of the interval (the geofence assignment endpoint sends a silent one).
  useEffect(() => {
    if (refreshKey === 0) return; // initial value; the bootstrap already fetched
    refreshMe();
  }, [refreshKey, refreshMe]);

  const login = (userData, token) => {
    setUser(userData);
    setAccessToken(token);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("access_token", token);
  };

  const logout = () => {
    setUser(null);
    setAccessToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
  };

  const updateUser = (next) => {
    setUser(next);
    localStorage.setItem("user", JSON.stringify(next));
  };

  // Backend roles: 'super_admin' | 'admin' | 'customer' | 'user' (employees).
  // Map 'user' → 'employee' for UI convenience.
  const rawRole = user?.role || null;
  const role =
    rawRole === "super_admin" || rawRole === "admin" || rawRole === "customer"
      ? rawRole
      : rawRole
      ? "employee"
      : null;

  // Admins (and the platform super admin) are super-users on the server;
  // mirror that here so checks stay identical between roles. Employees
  // carry the array straight from /me.
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  const hasPermission = (name) =>
    role === "admin" || role === "super_admin" || permissions.includes(name);
  const hasAnyPermission = (...names) =>
    role === "admin" || role === "super_admin" || names.some((n) => permissions.includes(n));

  // Active subscription addon codes for the firm (e.g. 'chat', 'chat_extended').
  // Unlike permissions, addons are NOT auto-granted to admins — gating is the
  // same for everyone in the firm.
  const addons = Array.isArray(user?.addons) ? user.addons : [];
  const hasAddon = (...codes) => codes.some((c) => addons.includes(c));

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        isLoading,
        isAuthenticated: !!accessToken,
        role,
        rawRole,
        permissions,
        hasPermission,
        hasAnyPermission,
        addons,
        hasAddon,
        login,
        logout,
        updateUser,
        refreshMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
